import React, { useMemo } from 'react'
import { ClipLoader } from 'react-spinners'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { DashboardData, GrantYearStat } from '../types'

interface Position {
  left?: number
  right?: number
  top?: number
  bottom?: number
}

interface Size {
  viewSize: any[]
}

interface DashBoardLineChartProps {
  mode: 'grantAmount' | 'grantMoneyAmount'
  stats: DashboardData
  isApiRequestInProgress: boolean
}

const DashBoardLineChart = ({ mode, stats, isApiRequestInProgress }: DashBoardLineChartProps): JSX.Element => {
  const formattedGrantAwardAmountByYearStats: GrantYearStat[] = useMemo(() => {
    if (!stats || !stats.grantByYearStats) {
      return []
    }

    const entries: GrantYearStat[] = [...stats.grantByYearStats].sort((itemA, itemB) => +itemA.year - +itemB.year)

    if (!entries.length) {
      return []
    }

    const currentYear = new Date().getFullYear()
    const maxStartYear: number = currentYear - 10

    const firstRecord: GrantYearStat = entries[0]
    const startYear: number = entries.length ? Math.min(maxStartYear, +firstRecord.year) : maxStartYear

    const entriesToReturn: GrantYearStat[] = []

    for (let i = startYear; i < currentYear + 1; i++) {
      const entry = entries.find((item) => +item.year === i)

      if (entry) {
        entriesToReturn.push(entry)
      } else {
        entriesToReturn.push({
          year: String(i),
          grantAmount: 0,
          moneyAwarded: 0,
        })
      }
    }

    return entriesToReturn
  }, [stats])

  const grantByYearTimestamps = useMemo(() => {
    return formattedGrantAwardAmountByYearStats.map((item) => item.year)
  }, [formattedGrantAwardAmountByYearStats])

  const grantAmountByYear = useMemo(() => {
    return formattedGrantAwardAmountByYearStats.map((item) => item.grantAmount)
  }, [formattedGrantAwardAmountByYearStats])

  const grantAwardedMoneyByYear = useMemo(() => {
    return formattedGrantAwardAmountByYearStats.map((item) => item.moneyAwarded)
  }, [formattedGrantAwardAmountByYearStats])

  const maxYearByYearGrantAmountAxisValue = useMemo(() => {
    return grantAmountByYear.length ? Math.max(...grantAmountByYear, 5) : 50
  }, [grantAmountByYear])

  const maxYearByYearGrantAwardAxisValue = useMemo(() => {
    return grantAwardedMoneyByYear.length ? Math.max(...grantAwardedMoneyByYear) : 50
  }, [grantAwardedMoneyByYear])

  const handleTooltipPosition = (
    point: number[],
    params: object | any[],
    dom: HTMLElement,
    rect: object,
    size: Size,
  ): Position | null => {
    // only for mobile device
    if (window.innerWidth <= 540) {
      const tooltipHeight = dom.offsetHeight
      const obj: Position = { top: point[1] - tooltipHeight - 20 }

      const posProp = point[0] < size.viewSize[0] / 2 ? 'left' : 'right'

      obj[posProp] = 5

      return obj
    }

    return null
  }
  const tooltipFormatter = (params: Array<Record<string, any>>): string => {
    let tooltipItem = ``

    params.forEach((el) => {
      tooltipItem += `<div class='ms-1'>
        <h6 class="text-700"><span class="fas fa-circle me-1 fs--2" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${typeof el.value === 'object' ? el.value[1] : el.value}
        </h6>
      </div>`
    })

    return `<div>
            <p class='mb-2 text-600'>
              ${params[0].axisValue}
            </p>
            ${tooltipItem}
          </div>`
  }

  const grantByYearStatChartOptions = useMemo(() => {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#8a94ad',
          },
          label: {
            show: true,
            backgroundColor: '#6e7891',
            color: '#eff2f6',
          },
        },
        padding: [7, 10],
        backgroundColor: '#eff2f6',
        borderColor: '#cbd0dd',
        textStyle: { color: '#141824' },
        borderWidth: 1,
        transitionDuration: 0,
        position: (point: number[], params: object | any[], dom: HTMLElement, rect: object, size: Size) =>
          handleTooltipPosition(point, params, dom, rect, size),
        formatter: (params: Array<Record<string, any>>) => tooltipFormatter(params),
      },
      toolbox: {
        top: 0,
        feature: {
          dataView: { show: false },
          magicType: {
            show: true,
            type: ['line', 'bar'],
          },
          restore: { show: false },
          saveAsImage: { show: true },
        },
        iconStyle: {
          borderColor: '#525b75',
          borderWidth: 1,
        },
        emphasis: {
          iconStyle: {
            textFill: '#6e7891',
          },
        },
      },
      legend: {
        top: 40,
        data: [mode === 'grantAmount' ? 'Grants Awarded' : 'Grant Money Awarded'],
        textStyle: {
          color: '#6e7891',
        },
      },
      xAxis: [
        {
          type: 'category',
          data: grantByYearTimestamps,
          axisLabel: {
            color: '#6e7891',
            formatter: (value: string) => value.slice(0, 4),
          },
          axisPointer: {
            type: 'shadow',
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#cbd0dd',
            },
          },
        },
      ],
      yAxis: [
        mode === 'grantAmount'
          ? {
              type: 'value',
              min: 0,
              axisLabel: {
                color: '#6e7891',
                formatter: '{value}',
              },
              splitLine: {
                show: false,
                lineStyle: {
                  color: '#31374a',
                },
              },
            }
          : {
              type: 'value',
              min: 0,
              axisLabel: {
                color: '#6e7891',
                formatter: '${value}',
              },
              splitLine: {
                show: false,
                lineStyle: {
                  color: '#e3e6ed',
                },
              },
            },
      ],
      series: [
        mode === 'grantAmount'
          ? {
              name: 'Grants Awarded',
              type: 'line',
              yAxisIndex: 0,
              data: grantAmountByYear,
              lineStyle: {
                color: '#ffcc85',
              },
              itemStyle: {
                color: 'white',
                borderColor: '#ffcc85',
                borderWidth: 2,
              },
              symbol: 'circle',
              symbolSize: 10,
            }
          : {
              name: 'Grant Money Awarded',
              type: 'line',
              yAxisIndex: 0,
              data: grantAwardedMoneyByYear,
              lineStyle: {
                color: '#ffcc85',
              },
              itemStyle: {
                color: 'white',
                borderColor: '#ffcc85',
                borderWidth: 2,
              },
              symbol: 'circle',
              symbolSize: 10,
            },
      ],
      grid: {
        right: 5,
        left: 5,
        bottom: 5,
        top: '23%',
        containLabel: true,
      },
    }
  }, [
    mode,
    grantByYearTimestamps,
    grantAmountByYear,
    grantAwardedMoneyByYear,
    maxYearByYearGrantAmountAxisValue,
    maxYearByYearGrantAwardAxisValue,
  ])

  return (
    <div style={{ minHeight: '320px', width: '100%' }}>
      {isApiRequestInProgress ? (
        <div className="spinner-wrapper mt-5">
          <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={35} />
        </div>
      ) : (
        <ReactEChartsCore echarts={echarts} option={grantByYearStatChartOptions} />
      )}
    </div>
  )
}

export default DashBoardLineChart
